import { useEffect, useRef, useState } from "react";

// Viewport/zoom/pan concerns for MapCanvas: container sizing, desktop scroll-to-zoom, and
// two-finger pinch-to-zoom for touch devices. Extracted verbatim from MapCanvas.jsx (Phase 42
// refactor) — no behavior changes, just isolated into its own hook.
export function useMapViewport() {
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ width: 800, height: 600 });
  const [scale, setScale] = useState(1);
  const [isTouchDevice] = useState(() => typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches);
  const lastPinchDist = useRef(0);
  const lastPinchCenter = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const resize = () => setDims({ width: el.clientWidth, height: el.clientHeight });
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const scaleBy = 1.08;
    const oldScale = e.target.getStage().scaleX();
    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    setScale(Math.max(0.3, Math.min(3, newScale)));
  };

  // Two-finger pinch-to-zoom for touch devices — desktop keeps scroll-to-zoom above, mobile gets
  // this instead. Standard Konva multi-touch pattern: track the distance+midpoint between the two
  // touches frame-to-frame and adjust stage scale/position so the pinch feels anchored under the fingers.
  const getTouchDistance = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  const getTouchCenter = (t1, t2) => ({ x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 });

  const handleTouchMove = (e) => {
    const touch1 = e.evt.touches[0];
    const touch2 = e.evt.touches[1];
    if (!touch1 || !touch2) return;
    e.evt.preventDefault();
    const stage = e.target.getStage();
    if (stage.isDragging()) stage.stopDrag();
    const center = getTouchCenter(touch1, touch2);
    const dist = getTouchDistance(touch1, touch2);
    if (!lastPinchCenter.current) {
      lastPinchCenter.current = center;
      lastPinchDist.current = dist;
      return;
    }
    const oldScale = stage.scaleX();
    const pointTo = { x: (center.x - stage.x()) / oldScale, y: (center.y - stage.y()) / oldScale };
    const newScale = Math.max(0.3, Math.min(3, oldScale * (dist / lastPinchDist.current)));
    const dx = center.x - lastPinchCenter.current.x;
    const dy = center.y - lastPinchCenter.current.y;
    stage.position({ x: center.x - pointTo.x * newScale + dx, y: center.y - pointTo.y * newScale + dy });
    setScale(newScale);
    lastPinchDist.current = dist;
    lastPinchCenter.current = center;
  };

  const handleTouchEnd = () => {
    lastPinchCenter.current = null;
    lastPinchDist.current = 0;
  };

  // Konva's own single-finger `draggable` pan can already be mid-gesture by the time a second
  // finger lands (it starts tracking off the very first touchstart) — if we don't explicitly
  // cancel it right here, its native touchmove handling keeps fighting our manual pinch math
  // every frame, which is what made pinch-zoom "not work" (jump/freeze) on real touch devices.
  const handleTouchStart = (e) => {
    if (e.evt.touches.length >= 2) {
      e.evt.preventDefault();
      const stage = e.target.getStage();
      if (stage.isDragging()) stage.stopDrag();
      lastPinchCenter.current = null;
      lastPinchDist.current = 0;
    }
  };

  return { containerRef, dims, scale, isTouchDevice, handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd };
}
